'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Phone } from 'lucide-react';
import { fetchSettings } from '@/lib/supabase/dinauto-service';

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Our Cars', href: '/our-cars-gallery' },
  { label: 'Reservation', href: '/reservation' },
  { label: 'Contact', href: '/contact' },
];

export default function PublicHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [phone, setPhone] = useState('+47 4412 673 82');
  const [address, setAddress] = useState('Munkebekken 400, 1061 Oslo — Org. 911696282');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetchSettings()?.then((s) => {
      if (s?.['company_phone']) setPhone(s?.['company_phone']);
      const addr = s?.['company_address'] || '';
      const org = s?.['company_org'] || '';
      if (addr) setAddress(`${addr}${org ? ` — Org. ${org}` : ''}`);
    });
  }, []);

  const telHref = `tel:${phone?.replace(/\s/g, '')}`;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-shadow duration-300"
      style={{
        backgroundColor: '#1a1a1a',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
      }}
    >
      {/* Top bar — address only, no phone (phone shown once in main nav) */}
      <div className={`border-b border-white/10 transition-all duration-300 ${scrolled ? 'hidden' : 'block'}`}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex items-center justify-between h-9">
          <span className="text-white/60 text-xs font-medium">{address}</span>
        </div>
      </div>
      {/* Main nav */}
      <nav className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        <Link href="/" className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="text-white font-black text-xl tracking-tight" style={{ fontWeight: 900 }}>DIN AUTO</span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-1">
          {NAV_LINKS?.map((link) => (
            <li key={`nav-${link?.href}`}>
              <Link
                href={link?.href}
                className="px-4 py-2 text-white/80 hover:text-white text-sm font-600 transition-colors hover:bg-white/8"
                style={{ fontWeight: 600 }}
              >
                {link?.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <a href={telHref} className="flex items-center gap-1.5 text-primary text-sm font-bold hover:text-primary/80 transition-colors">
            <Phone size={14} />
            <span>{phone}</span>
          </a>
          <Link href="/reservation" className="btn-primary text-sm px-5 py-2.5">
            Reserve Now
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>
      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-white/10 animate-fade-in" style={{ backgroundColor: '#1a1a1a' }}>
          <ul className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS?.map((link) => (
              <li key={`mobile-nav-${link?.href}`}>
                <Link
                  href={link?.href}
                  className="block py-3 text-white/80 hover:text-white text-base font-600 border-b border-white/8 transition-colors"
                  style={{ fontWeight: 600 }}
                  onClick={() => setMenuOpen(false)}
                >
                  {link?.label}
                </Link>
              </li>
            ))}
            <li className="pt-2 pb-1">
              <a href={telHref} className="flex items-center gap-2 py-3 text-primary font-bold border-b border-white/8">
                <Phone size={16} />
                {phone}
              </a>
            </li>
            <li className="pt-3">
              <Link href="/reservation" className="btn-primary w-full justify-center" onClick={() => setMenuOpen(false)}>
                Reserve Now
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}